import {
  FileCheckIcon,
  FileExclamationPointIcon,
  FileXIcon,
  XIcon,
} from 'lucide-react'
import type { FileRejection } from 'react-dropzone'
import { tv } from 'tailwind-variants'

import { Button } from '@/components/ui'

const file = tv({
  slots: {
    iconWrapper: 'inline-flex rounded p-1',
    icon: 'size-5',
  },
  variants: {
    theme: {
      default: {
        iconWrapper: 'bg-brand/20',
        icon: 'text-brand',
      },
      rejected: {
        iconWrapper: 'bg-red-600/20',
        icon: 'text-red-600',
      },
    },
  },

  defaultVariants: {
    theme: 'default',
  },
})

interface FileItemProps {
  item: File | FileRejection
  hasError?: boolean
  maxSize?: number
  onRemove: () => void
}

export const FileItem = ({
  item,
  hasError,
  maxSize,
  onRemove,
}: FileItemProps) => {
  const isRejected =
    'errors' in item &&
    item.errors?.some((error) => error.code === 'file-too-large')

  const { iconWrapper, icon } = file({
    theme: isRejected || hasError ? 'rejected' : 'default',
  })

  const Icon = isRejected
    ? FileXIcon
    : hasError
      ? FileExclamationPointIcon
      : FileCheckIcon

  return (
    <div className="flex gap-2 justify-between items-center border-b border-zinc-950/10 py-2 text-zinc-600 first:pt-0 last:border-0 last:pb-0 data-[hover]:border-zinc-950/20">
      <div className="flex gap-2 items-center">
        <div className="self-stretch">
          <span className={iconWrapper()}>
            <Icon className={icon()} />
          </span>
        </div>
        <div className="flex flex-col gap-0">
          <span className="flex-1">
            {isRejected ? item.file.name : (item as File).name}
          </span>
          {isRejected && (
            <div className="flex items-center gap-1">
              <span className="text-red-600 text-xs">
                {maxSize
                  ? `Size limit of ${maxSize} MB exceeded.`
                  : 'File is too large.'}
              </span>
            </div>
          )}
        </div>
      </div>

      <Button
        onClick={onRemove}
        theme="ghost"
        size="sm"
        isIcon
        className="shrink-0"
      >
        <XIcon className="fill-zinc-500 size-5" />
      </Button>
    </div>
  )
}
